// utils/formatters.js

export function getKeyMetrics(data, columnTypes) {
  if (!Array.isArray(data) || data.length === 0 || typeof columnTypes !== 'object') return [];

  const formatPercent = (val) => (val > 0 ? '+' : '') + (val * 100).toFixed(1) + '%';
  const latestIdx = data.length - 1;
  const prevIdx = data.length - 2;
  const latest = data[latestIdx] || {};
  const prev = prevIdx >= 0 ? data[prevIdx] || {} : null;

  // Accept both array style (["colA","colB"]) and object-map style ({ "Total Revenue": {...} })
  const getColumnKey = (type, fallbacks = []) => {
    const v = columnTypes?.[type];
    // array form e.g. columnTypes.revenue = ["Total Revenue", "MRR"]
    if (Array.isArray(v) && v.length > 0) return v[0];
    // object-map form e.g. columnTypes = { "Total Revenue": { type:"Revenue", label:"Total Revenue" }, ... }
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const first = Object.keys(v)[0];
      if (first) return first;
    }
    // try provided fallbacks by presence in latest row
    for (const label of fallbacks) {
      if (label in latest) return label;
    }
    return null;
  };

  // Use space-named canonical columns first, then snake_case as legacy fallbacks
  const revenueKey = getColumnKey('revenue', ['Total Revenue', 'Total_Revenue', 'Revenue']);
  const expenseKey = getColumnKey('expense', ['Total Expenses', 'Total_Expenses', 'Expenses']);
  const profitKey  = getColumnKey('profit',  ['Net Income', 'Profit', 'Net_Income']);


  const kpis = [];

  const priority = ['profit', 'revenue', 'cash', 'asset', 'equity', 'eps', 'margin', 'roe', 'roa', 'expense', 'debt', 'growth'];

  const flatColumnMap = Object.entries(columnTypes).flatMap(([key, value]) => {
    if (Array.isArray(value)) {
      // e.g. columnTypes.revenue = [ "Total Revenue", "Productivity..." ]
      return value.map((col) => [col, { type: key }]);
    } else if (typeof value === 'object') {
      // e.g. columnTypes.other = { "Basic EPS": { type: "Other", label: "EPS" }, ... }
      return Object.entries(value);
    }
    return [];
  });

  const filtered = flatColumnMap
    .filter(([col, meta]) =>
      typeof latest[col] === 'number' &&
      !col.toLowerCase().includes('employee') &&
      (meta?.type || meta?.label)
    )
    .sort((a, b) => {
      const aIndex = priority.indexOf(a[1].type?.toLowerCase());
      const bIndex = priority.indexOf(b[1].type?.toLowerCase());
      return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
    })
    .slice(0, 10);




  filtered.forEach(([col, meta]) => {
    const curr = latest[col];
    const prevVal = prev?.[col];
    if (typeof curr !== 'number') return;

    const delta = prevVal && typeof prevVal === 'number' ? (curr - prevVal) / prevVal : null;
    const label = meta.label || col;
    const value = formatValue(curr, label);
    const trend = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';

    kpis.push({
      label,
      value,
      delta: delta !== null ? formatPercent(delta) : '–',
      trend,
      unit: label.toLowerCase().includes('employee') ? '' : '$'
    });
  });

  const pushMetric = (key) => {
    if (!key || !(key in latest)) return;
    const curr = latest[key];
    const prevVal = prev?.[key];
    const delta = prevVal && typeof prevVal === 'number' ? (curr - prevVal) / prevVal : null;
    kpis.push({
      label: key.replace(/_/g, ' '),
      value: curr !== null ? formatValue(curr) : 'N/A',
      delta: delta !== null ? formatPercent(delta) : '–',
      trend: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
      unit: '$'
    });
  };

  pushMetric(revenueKey);
  pushMetric(expenseKey);
  pushMetric(profitKey);

  return kpis;
}

export function getTrendInsights(data, columnTypes) {
  const insights = [];
  if (!Array.isArray(data) || data.length < 3 || typeof columnTypes !== 'object') return insights;

  const latest = data[data.length - 1];
  const prev1 = data[data.length - 2];
  const prev2 = data[data.length - 3];

  const priority = ['profit', 'revenue', 'cash', 'asset', 'equity', 'eps', 'margin', 'roe', 'roa', 'expense'];

  const flatColumnMap = Object.entries(columnTypes).flatMap(([key, value]) => {
    if (Array.isArray(value)) {
      return value.map((col) => [col, { type: key }]);
    } else if (typeof value === 'object') {
      return Object.entries(value);
    }
    return [];
  });

  const topCols = flatColumnMap
    .filter(([col]) => typeof latest[col] === 'number' && typeof prev1[col] === 'number' && typeof prev2[col] === 'number')
    .sort((a, b) => {
      const aIdx = priority.indexOf(a[1]?.type?.toLowerCase());
      const bIdx = priority.indexOf(b[1]?.type?.toLowerCase());
      return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
    })
    .slice(0, 10);

  const pct = (curr, prev) => {
    if (!prev || prev === 0) return null;
    return ((curr - prev) / prev * 100).toFixed(1);
  };

  topCols.forEach(([col]) => {
    const q1 = prev2[col];
    const q2 = prev1[col];
    const q3 = latest[col];

    const change1 = pct(q2, q1);
    const change2 = pct(q3, q2);

    const label = col.replace(/_/g, ' ');

    if (change1 !== null && change2 !== null) {
      if (Math.abs(change2) >= 2 || Math.abs(change1) >= 2) {
        const trend = change2 > 0 ? 'increased' : 'decreased';
        const priorTrend = change1 > 0 ? 'rose' : 'fell';
        insights.push(`${label} ${trend} by ${Math.abs(change2)}% from Q2 to Q3 (previously ${priorTrend} by ${Math.abs(change1)}% from Q1 to Q2).`);
      }
    }
  });

  return insights;
}


export function formatCurrency(value, digits = 1) {
  if (value === null || value === undefined || isNaN(value)) return '—';
  const num = parseFloat(value);
  if (!isFinite(num)) return '—';
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(digits)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(digits)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(digits)}K`;
  return `${sign}$${abs.toFixed(digits)}`;
}

export function formatValue(value, decimals = 2) {
  if (value === null || value === undefined || isNaN(value)) return '—';
  const num = parseFloat(value);
  if (!isFinite(num)) return '—';
  return num.toFixed(decimals);
}


export function formatMoney(amount, scale = 'billions', currency = '$') {
  if (typeof amount !== 'number' || isNaN(amount)) return amount;

  const scaleMap = {
    thousands: { divisor: 1_000, suffix: 'K' },
    millions: { divisor: 1_000_000, suffix: 'M' },
    billions: { divisor: 1_000_000_000, suffix: 'B' }
  };

  const { divisor, suffix } = scaleMap[scale] || { divisor: 1, suffix: '' };
  const scaledAmount = amount / divisor;

  return `${currency}${scaledAmount.toFixed(1)}${suffix}`;
}


export function formatPercent(value) {
  if (value === null || value === undefined) return 'N/A';
  return `${(value * 100).toFixed(1)}%`;
}

